import { useState } from "react";
import { Minus, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { createRsvp, lookupRsvp, updateRsvp, cancelRsvp } from "../lib/api";
import { Confirmation } from "./Confirmation";
import { Reveal } from "./Reveal";

const EMPTY = { name: "", phone: "", party_size: 1, attendees: [], allergies: "", considerations: "", notes: "" };

const notifyBoard = () => window.dispatchEvent(new Event("rsvp-updated"));

const errMsg = (err, fallback) => {
  const d = err.response?.data?.detail;
  return Array.isArray(d) ? d[0]?.msg || fallback : d || fallback;
};

const RsvpFields = ({ form, setForm }) => {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setAttendee = (i, key) => (e) =>
    setForm((f) => {
      const attendees = [...f.attendees];
      attendees[i] = { name: "", soup: "", ...attendees[i], [key]: e.target.value };
      return { ...f, attendees };
    });
  const shrinkParty = () =>
    setForm((f) => {
      const size = Math.max(1, f.party_size - 1);
      return { ...f, party_size: size, attendees: f.attendees.slice(0, size) };
    });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7">
      <div>
        <label className="field-label" htmlFor="rsvp-name">Your Name *</label>
        <input id="rsvp-name" data-testid="rsvp-name-input" className="field" placeholder="Aunt June" value={form.name} onChange={set("name")} />
      </div>
      <div>
        <label className="field-label" htmlFor="rsvp-phone">Phone Number *</label>
        <input id="rsvp-phone" data-testid="rsvp-phone-input" className="field" type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
        <p className="mt-2 font-body text-xs text-fog/70">This is your key — use it to edit or cancel later. No passwords.</p>
      </div>
      <div>
        <label className="field-label">How Many People? *</label>
        <div className="flex items-center border border-forest/40" data-testid="rsvp-party-size">
          <button type="button" data-testid="party-size-minus" onClick={shrinkParty} className="px-4 py-3.5 text-fog hover:text-ember transition-colors duration-300">
            <Minus size={16} />
          </button>
          <span className="flex-1 text-center font-display text-2xl text-cream">{form.party_size}</span>
          <button type="button" data-testid="party-size-plus" onClick={() => setForm((f) => ({ ...f, party_size: Math.min(20, f.party_size + 1) }))} className="px-4 py-3.5 text-fog hover:text-ember transition-colors duration-300">
            <Plus size={16} />
          </button>
        </div>
      </div>
      <div className="md:col-span-2">
        <label className="field-label">Who's Coming & What They're Bringing *</label>
        <p className="mb-3 font-body text-xs text-fog/70">
          One row per person in your party — add each person's name, and the soup they're bringing next to it (handy when two people in the same house each want to bring one). At least one soup total, up to one per person.
        </p>
        {Array.from({ length: form.party_size }).map((_, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              data-testid={`rsvp-guest-name-${i}`}
              className="field"
              placeholder={i === 0 ? "Your name (again, for the list)" : `Guest ${i + 1} name (optional)`}
              value={form.attendees[i]?.name || ""}
              onChange={setAttendee(i, "name")}
            />
            <input
              data-testid={`rsvp-guest-soup-${i}`}
              className="field"
              placeholder={i === 0 ? "Your soup — e.g. smoky butternut squash" : `Guest ${i + 1}'s soup (optional)`}
              value={form.attendees[i]?.soup || ""}
              onChange={setAttendee(i, "soup")}
            />
          </div>
        ))}
      </div>
      <div>
        <label className="field-label" htmlFor="rsvp-allergies">Allergies in Your Party</label>
        <input id="rsvp-allergies" data-testid="rsvp-allergies-input" className="field" placeholder="Tree nuts, shellfish…" value={form.allergies} onChange={set("allergies")} />
        <p className="mt-2 font-body text-xs text-fog/70">Shown publicly on the Allergies board so cooks can plan.</p>
      </div>
      <div>
        <label className="field-label" htmlFor="rsvp-considerations">Considerations</label>
        <input id="rsvp-considerations" data-testid="rsvp-considerations-input" className="field" placeholder="Vegetarian, low sodium, spice level…" value={form.considerations} onChange={set("considerations")} />
      </div>
      <div className="md:col-span-2">
        <label className="field-label" htmlFor="rsvp-notes">Anything Else</label>
        <textarea id="rsvp-notes" data-testid="rsvp-notes-input" className="field min-h-[96px] resize-y" placeholder="Bringing folding chairs, arriving a little late, kid in tow — anything at all." value={form.notes} onChange={set("notes")} />
      </div>
    </div>
  );
};

export const RsvpSection = ({ party }) => {
  const [tab, setTab] = useState("create");
  const [form, setForm] = useState(EMPTY);
  const [lookupPhone, setLookupPhone] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [busy, setBusy] = useState(false);

  const valid = form.name.trim() && form.phone.replace(/\D/g, "").length >= 7 && form.attendees.some((a) => a.soup.trim());
  const payload = () => ({ ...form, attendees: form.attendees.filter((a) => a.name.trim() || a.soup.trim()) });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      const rsvp = await createRsvp(payload());
      setConfirmed(rsvp);
      notifyBoard();
    } catch (err) {
      toast.error(errMsg(err, "Something went wrong — try again"));
    } finally {
      setBusy(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const rsvp = await lookupRsvp(lookupPhone);
      setEditing(rsvp);
      setForm({ name: rsvp.name, phone: rsvp.phone, party_size: rsvp.party_size, attendees: rsvp.attendees || [], allergies: rsvp.allergies, considerations: rsvp.considerations, notes: rsvp.notes });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No RSVP found for that number");
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!valid || busy || !editing) return;
    setBusy(true);
    try {
      const rsvp = await updateRsvp(editing.id, payload());
      setConfirmed(rsvp);
      setEditing(null);
      notifyBoard();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't update — try again"));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      await cancelRsvp(editing.phone);
      toast.success("You're un-RSVP'd. You'll be missed!");
      setEditing(null);
      setForm(EMPTY);
      setLookupPhone("");
      setTab("create");
      notifyBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel — try again");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setConfirmed(null);
    setEditing(null);
    setForm(EMPTY);
    setLookupPhone("");
    setTab("create");
  };

  return (
    <section id="rsvp" className="relative px-6 md:px-16 lg:px-24 py-24 md:py-36" data-testid="rsvp-section">
      <Reveal x={-80}>
        <p className="font-body text-[11px] uppercase tracking-[0.35em] text-ember mb-4">Save Your Seat</p>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream">
          RSVP — <span className="font-accent text-harvest text-4xl md:text-6xl align-middle -rotate-2 inline-block">no account needed.</span>
        </h2>
        <p className="mt-5 font-body text-base md:text-lg text-fog leading-relaxed max-w-2xl">
          Your phone number is your ticket in. Need to change plans? Come back, enter the same number, and edit or un-RSVP in seconds.
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-14 md:mt-20">
        {confirmed ? (
          <Confirmation rsvp={confirmed} party={party} onDone={reset} />
        ) : (
          <div className="notice-block p-8 md:p-12" data-testid="rsvp-form-container">
            <div className="flex gap-2 mb-10 border-b border-forest/40 pb-5">
              <button
                data-testid="tab-create-rsvp"
                onClick={() => { setTab("create"); setEditing(null); setForm(EMPTY); }}
                className={`font-body text-xs font-semibold uppercase tracking-[0.22em] px-5 py-2.5 rounded-full transition-colors duration-300 ${tab === "create" ? "bg-ember text-[#FBF4E8]" : "text-fog hover:text-cream"}`}
              >
                New RSVP
              </button>
              <button
                data-testid="tab-manage-rsvp"
                onClick={() => setTab("manage")}
                className={`font-body text-xs font-semibold uppercase tracking-[0.22em] px-5 py-2.5 rounded-full transition-colors duration-300 ${tab === "manage" ? "bg-ember text-[#FBF4E8]" : "text-fog hover:text-cream"}`}
              >
                Manage / Un-RSVP
              </button>
            </div>

            {tab === "create" && (
              <form onSubmit={handleCreate}>
                <RsvpFields form={form} setForm={setForm} />
                <div className="mt-10">
                  <button data-testid="rsvp-submit-button" type="submit" disabled={!valid || busy} className="btn-ember disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? "Saving…" : "Count Us In"}
                  </button>
                </div>
              </form>
            )}

            {tab === "manage" && !editing && (
              <form onSubmit={handleLookup} className="max-w-md">
                <label className="field-label" htmlFor="lookup-phone">Enter the phone number you RSVP'd with</label>
                <div className="flex gap-3">
                  <input id="lookup-phone" data-testid="manage-phone-input" className="field" type="tel" placeholder="(555) 123-4567" value={lookupPhone} onChange={(e) => setLookupPhone(e.target.value)} />
                  <button data-testid="manage-lookup-btn" type="submit" disabled={busy} className="btn-ember !px-6 shrink-0">
                    <Search size={16} />
                  </button>
                </div>
              </form>
            )}

            {tab === "manage" && editing && (
              <form onSubmit={handleUpdate} data-testid="manage-edit-form">
                <p className="mb-8 font-accent text-3xl text-harvest -rotate-1">welcome back, {editing.name.split(" ")[0]}!</p>
                <RsvpFields form={form} setForm={setForm} />
                <div className="mt-10 flex flex-wrap gap-4">
                  <button data-testid="rsvp-update-button" type="submit" disabled={!valid || busy} className="btn-ember disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? "Saving…" : "Save Changes"}
                  </button>
                  <button
                    data-testid="rsvp-cancel-button"
                    type="button"
                    onClick={handleCancel}
                    disabled={busy}
                    className="btn-ghost !border-berry !text-berry hover:!text-cream hover:!bg-berry"
                  >
                    <Trash2 size={16} />
                    Un-RSVP
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </Reveal>
    </section>
  );
};
